import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

export default function ListeFacturationsPage() {
  const navigate = useNavigate();
  const { utilisateur } = useAuth();
  const [facturations, setFacturations] = useState([]);
  const [abattoirs, setAbattoirs] = useState([]);
  const [message, setMessage] = useState({ type: '', texte: '' });
  const today = new Date();
  const debutMois = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
  const finMois = new Date(today.getFullYear(), today.getMonth()+1, 0).toISOString().split('T')[0];
  const [filtreAbattoir, setFiltreAbattoir] = useState('');
  const [filtreDebut, setFiltreDebut] = useState(debutMois);
  const [filtreFin, setFiltreFin] = useState(finMois);
  const [page, setPage] = useState(1);
  const [hoveredRow, setHoveredRow] = useState(null);
  const [itemsParPage, setItemsParPage] = useState(10);

  useEffect(() => {
    api.get('/facturation').then(r => setFacturations(r.data));
    api.get('/abattoirs').then(r => setAbattoirs(r.data));
  }, []);

  const signer = async (id) => {
    if (!window.confirm('Signer cette facturation ?')) return;
    try {
      await api.put(`/facturation/${id}/signer`);
      setMessage({ type: 'succes', texte: '✅ Facturation signée !' });
      const r = await api.get('/facturation');
      setFacturations(r.data);
    } catch (err) {
      setMessage({ type: 'erreur', texte: err.response?.data?.message || 'Erreur.' });
    }
  };

  const filtrees = facturations.filter(f => {
    const d = new Date(f.date_facturation);
    if (filtreDebut && d < new Date(filtreDebut)) return false;
    if (filtreFin && d > new Date(filtreFin)) return false;
    if (filtreAbattoir && f.abattoir_id !== parseInt(filtreAbattoir)) return false;
    return true;
  });

  const totalPages = Math.max(1, Math.ceil(filtrees.length / itemsParPage));
  const pageData = filtrees.slice((page-1)*itemsParPage, page*itemsParPage);

  const totaux = filtrees.reduce((a, f) => ({
    bovins: a.bovins + parseFloat(f.bovins_stab_jour||0) + parseFloat(f.bovins_stab_nuit||0) + parseFloat(f.bovins_abattage||0) + parseFloat(f.bovins_ressuage||0),
    ovins: a.ovins + parseFloat(f.ovins_stab||0) + parseFloat(f.ovins_abattage||0),
    caprins: a.caprins + parseFloat(f.caprins_stab||0) + parseFloat(f.caprins_abattage||0),
    porcins: a.porcins + parseFloat(f.porcins_stab||0) + parseFloat(f.porcins_abattage||0),
    annexes: a.annexes + parseFloat(f.stockage_frigo||0) + parseFloat(f.stockage_abattoir||0) + parseFloat(f.location||0) + parseFloat(f.timbres||0),
  }), { bovins:0, ovins:0, caprins:0, porcins:0, annexes:0 });

  const fmt = (n) => Math.round(n).toLocaleString('fr-FR');
  const formatDate = (d) => new Date(d).toLocaleDateString('fr-FR');

  const C = {
    primary: '#2E7D32', primaryDark: '#1B5E20',
    border: '#E0E0E0', light: '#F4F6F9',
    green50: '#F1F8F2', green100: '#E8F5E9',
    white: '#FFFFFF', textDark: '#212121', textMid: '#616161',
    grayLight: '#9E9E9E',
  };

  const badge = (s) => ({
    padding: '4px 12px', borderRadius: 20, fontSize: 11, fontWeight: 700,
    whiteSpace: 'nowrap',
    background: s === 'SIGNE' ? C.green100 : '#F5F5F5',
    color:      s === 'SIGNE' ? C.primaryDark : '#757575',
  });

  const badgeJour = (tj) => ({
    padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 700, marginLeft: 6,
    background: tj === 'SPECIAL' ? '#FFF3E0' : C.green100,
    color:      tj === 'SPECIAL' ? '#E65100' : C.primary,
  });

  const th = { padding: '10px 10px', fontSize: 11, fontWeight: 700, color: C.textMid,
    textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'center',
    whiteSpace: 'nowrap', borderBottom: `2px solid ${C.border}`, background: '#F8F9FA' };
  const td = { padding: '10px 10px', borderBottom: `1px solid ${C.border}`,
    textAlign: 'center', verticalAlign: 'middle', fontSize: 13 };
  const tdLeft = { ...td, textAlign: 'left' };

  const inputStyle = { width: '100%', padding: '8px 10px', border: `1px solid ${C.border}`,
    borderRadius: 8, fontSize: 13, boxSizing: 'border-box', outline: 'none' };
  const labelStyle = { display: 'block', fontSize: 12, color: C.textMid, marginBottom: 4, fontWeight: 600 };

  return (
    <div style={{ background: C.light, minHeight: '100vh', fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", paddingBottom: 40 }}>

      {/* Topbar */}
      <div style={{ background: C.white, borderBottom: `1px solid ${C.border}`, padding: '0 24px', height: 52,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        position: 'sticky', top: 0, zIndex: 10, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
        <div style={{ fontSize: 13, color: C.textMid }}>
          Facturation
          <span style={{ margin: '0 6px', color: C.grayLight }}>›</span>
          <span style={{ color: C.textDark, fontWeight: 600 }}>Liste des facturations</span>
        </div>
        <button
          onClick={() => navigate('/facturation/nouvelle')}
          style={{ background: C.primary, color: C.white, border: 'none', borderRadius: 8,
            padding: '7px 16px', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
          + Nouvelle facturation
        </button>
      </div>

      <div style={{ padding: '20px 24px' }}>
        {message.texte && (
          <div style={{ padding: '10px 16px', borderRadius: 8, marginBottom: 16, fontSize: 13,
            background: message.type === 'succes' ? C.green100 : '#FFEBEE',
            color: message.type === 'succes' ? C.primaryDark : '#C62828',
            border: `1px solid ${message.type === 'succes' ? '#A5D6A7' : '#FFCDD2'}` }}>
            {message.texte}
          </div>
        )}

        {/* Filtres */}
        <div style={{ background: C.white, borderRadius: 12, border: `1px solid ${C.border}`,
          boxShadow: '0 1px 3px rgba(0,0,0,0.06)', padding: '16px 20px', marginBottom: 16 }}>
          <div style={{ fontWeight: 700, color: C.textDark, fontSize: 13, marginBottom: 12 }}>Filtres</div>
          <div style={{ display: 'grid', gridTemplateColumns: utilisateur?.role === 'admin' ? '2fr 1fr 1fr auto' : '1fr 1fr auto', gap: 12, alignItems: 'end' }}>
            {utilisateur?.role === 'admin' && (
              <div>
                <label style={labelStyle}>Abattoir</label>
                <select style={inputStyle} value={filtreAbattoir} onChange={e => { setFiltreAbattoir(e.target.value); setPage(1); }}>
                  <option value="">Tous les abattoirs</option>
                  {abattoirs.map(a => <option key={a.id} value={a.id}>{a.nom}</option>)}
                </select>
              </div>
            )}
            <div>
              <label style={labelStyle}>Date début</label>
              <input style={inputStyle} type="date" value={filtreDebut} onChange={e => { setFiltreDebut(e.target.value); setPage(1); }} />
            </div>
            <div>
              <label style={labelStyle}>Date fin</label>
              <input style={inputStyle} type="date" value={filtreFin} onChange={e => { setFiltreFin(e.target.value); setPage(1); }} />
            </div>
            <div>
              <button style={{ background: C.white, color: C.textMid, border: `1px solid ${C.border}`,
                padding: '8px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 13 }}
                onClick={() => { setFiltreAbattoir(''); setFiltreDebut(debutMois); setFiltreFin(finMois); setPage(1); }}>
                Réinitialiser
              </button>
            </div>
          </div>
        </div>

        {/* Tableau */}
        <div style={{ background: C.white, borderRadius: 12, border: `1px solid ${C.border}`,
          boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
          {filtrees.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 48, color: C.grayLight, fontSize: 14 }}>
              Aucune facturation trouvée.
            </div>
          ) : (
            <>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr>
                    <th style={{ ...th, textAlign: 'left' }}>Date</th>
                    <th style={{ ...th, textAlign: 'left' }}>Abattoir</th>
                    <th style={th}>Session</th>
                    <th style={th}>🐄 Bovins</th>
                    <th style={th}>🐑 Ovins</th>
                    <th style={th}>🐐 Caprins</th>
                    <th style={th}>🐖 Porcins</th>
                    <th style={th}>Annexes</th>
                    <th style={th}>Total FCFA</th>
                    <th style={th}>Statut</th>
                    <th style={th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pageData.map((f, idx) => {
                    const tBov = parseFloat(f.bovins_stab_jour||0)+parseFloat(f.bovins_stab_nuit||0)+parseFloat(f.bovins_abattage||0)+parseFloat(f.bovins_ressuage||0);
                    const tOvi = parseFloat(f.ovins_stab||0)+parseFloat(f.ovins_abattage||0);
                    const tCap = parseFloat(f.caprins_stab||0)+parseFloat(f.caprins_abattage||0);
                    const tPor = parseFloat(f.porcins_stab||0)+parseFloat(f.porcins_abattage||0);
                    const tAnn = parseFloat(f.stockage_frigo||0)+parseFloat(f.stockage_abattoir||0)+parseFloat(f.location||0)+parseFloat(f.timbres||0);
                    const total = tBov+tOvi+tCap+tPor+tAnn;
                    return (
                      <tr key={f.id}
                        onMouseEnter={() => setHoveredRow(f.id)}
                        onMouseLeave={() => setHoveredRow(null)}
                        style={{ background: hoveredRow === f.id ? C.green50 : (idx % 2 === 0 ? C.white : '#FAFAFA') }}>
                        <td style={tdLeft}>
                          <span style={{ fontWeight: 600, color: C.textDark }}>{formatDate(f.date_facturation)}</span>
                          <span style={badgeJour(f.type_jour)}>{f.type_jour}</span>
                        </td>
                        <td style={tdLeft}>{f.abattoir_nom}</td>
                        <td style={td}><span style={{ fontSize: 11, color: C.textMid }}>{f.session_label}</span></td>
                        <td style={td}>{fmt(tBov)}</td>
                        <td style={td}>{fmt(tOvi)}</td>
                        <td style={td}>{fmt(tCap)}</td>
                        <td style={td}>{fmt(tPor)}</td>
                        <td style={td}>{fmt(tAnn)}</td>
                        <td style={td}>
                          <span style={{ fontWeight: 700, color: C.primary, fontFamily: 'monospace' }}>{fmt(total)}</span>
                        </td>
                        <td style={td}><span style={badge(f.statut)}>{f.statut}</span></td>
                        <td style={td}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'center' }}>
                            {f.statut === 'BROUILLON' && (
                              <button style={{ background: C.white, color: C.textDark, border: `1px solid ${C.border}`,
                                borderRadius: 8, padding: '5px 12px', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}
                                onClick={() => navigate(`/facturation/${f.id}/modifier`)}>
                                ✏️ Modifier
                              </button>
                            )}
                            {f.statut === 'BROUILLON' && (
                              <button style={{ background: C.primary, color: C.white, border: 'none',
                                borderRadius: 8, padding: '5px 12px', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}
                                onClick={() => signer(f.id)}>
                                ✍️ Signer
                              </button>
                            )}
                            {f.statut === 'SIGNE' && ['admin'].includes(utilisateur?.role) && (
                              <button style={{ background: '#C62828', color: C.white, border: 'none',
                                borderRadius: 8, padding: '5px 12px', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}
                                onClick={() => navigate(`/facturation/${f.id}/modifier`)}>
                                🔑 Corriger
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {/* Ligne totaux */}
                  <tr style={{ background: C.green50, fontWeight: 700, borderTop: `2px solid ${C.border}` }}>
                    <td style={{ ...tdLeft, fontSize: 12, color: C.textMid }} colSpan="3">
                      TOTAUX ({filtrees.length} facturations)
                    </td>
                    <td style={{ ...td, fontWeight: 700 }}>{fmt(totaux.bovins)}</td>
                    <td style={{ ...td, fontWeight: 700 }}>{fmt(totaux.ovins)}</td>
                    <td style={{ ...td, fontWeight: 700 }}>{fmt(totaux.caprins)}</td>
                    <td style={{ ...td, fontWeight: 700 }}>{fmt(totaux.porcins)}</td>
                    <td style={{ ...td, fontWeight: 700 }}>{fmt(totaux.annexes)}</td>
                    <td style={{ ...td, fontWeight: 800, color: C.primaryDark, fontSize: 14 }}>
                      {fmt(totaux.bovins+totaux.ovins+totaux.caprins+totaux.porcins+totaux.annexes)}
                    </td>
                    <td style={td} colSpan="2"></td>
                  </tr>
                </tbody>
              </table>

              {/* Pagination Odoo style */}
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '10px 16px', borderTop: `1px solid ${C.border}`,
                background: '#FAFAFA', borderRadius: '0 0 12px 12px'
              }}>
                {/* Info gauche */}
                <div style={{ fontSize: 12, color: '#616161' }}>
                  {filtrees.length} facturation(s) · Page {page} sur {totalPages}
                </div>

                {/* Boutons navigation */}
                <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                  {/* Première page */}
                  <button onClick={() => setPage(1)} disabled={page === 1} style={{
                    width: 30, height: 30, border: `1px solid ${C.border}`, borderRadius: 6,
                    background: page === 1 ? '#F5F5F5' : '#fff', color: page === 1 ? '#BDBDBD' : '#616161',
                    cursor: page === 1 ? 'default' : 'pointer', fontSize: 13,
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}>«</button>
                  {/* Page précédente */}
                  <button onClick={() => setPage(p => Math.max(1, p-1))} disabled={page === 1} style={{
                    width: 30, height: 30, border: `1px solid ${C.border}`, borderRadius: 6,
                    background: page === 1 ? '#F5F5F5' : '#fff', color: page === 1 ? '#BDBDBD' : '#616161',
                    cursor: page === 1 ? 'default' : 'pointer', fontSize: 13,
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}>‹</button>

                  {/* Numéros de pages avec ellipsis */}
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
                    .reduce((acc, p, idx, arr) => {
                      if (idx > 0 && p - arr[idx-1] > 1) acc.push('...');
                      acc.push(p);
                      return acc;
                    }, [])
                    .map((p, idx) => p === '...' ? (
                      <span key={`sep-${idx}`} style={{
                        width: 30, height: 30, display: 'flex', alignItems: 'center',
                        justifyContent: 'center', fontSize: 13, color: '#9E9E9E'
                      }}>…</span>
                    ) : (
                      <button key={`pg-${p}`} onClick={() => setPage(p)} style={{
                        width: 30, height: 30, border: page === p ? 'none' : `1px solid ${C.border}`,
                        borderRadius: 6, background: page === p ? C.primary : '#fff',
                        color: page === p ? '#fff' : '#616161', fontWeight: page === p ? 700 : 400,
                        cursor: 'pointer', fontSize: 12,
                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                      }}>{p}</button>
                    ))
                  }

                  {/* Page suivante */}
                  <button onClick={() => setPage(p => Math.min(totalPages, p+1))} disabled={page === totalPages} style={{
                    width: 30, height: 30, border: `1px solid ${C.border}`, borderRadius: 6,
                    background: page === totalPages ? '#F5F5F5' : '#fff',
                    color: page === totalPages ? '#BDBDBD' : '#616161',
                    cursor: page === totalPages ? 'default' : 'pointer', fontSize: 13,
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}>›</button>
                  {/* Dernière page */}
                  <button onClick={() => setPage(totalPages)} disabled={page === totalPages} style={{
                    width: 30, height: 30, border: `1px solid ${C.border}`, borderRadius: 6,
                    background: page === totalPages ? '#F5F5F5' : '#fff',
                    color: page === totalPages ? '#BDBDBD' : '#616161',
                    cursor: page === totalPages ? 'default' : 'pointer', fontSize: 13,
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}>»</button>
                </div>

                {/* Sélecteur lignes par page */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 12, color: '#616161' }}>Lignes par page :</span>
                  <select
                    value={itemsParPage}
                    onChange={e => { setItemsParPage(Number(e.target.value)); setPage(1); }}
                    style={{
                      border: `1px solid ${C.border}`, borderRadius: 6, padding: '4px 8px',
                      fontSize: 12, color: '#616161', background: '#fff', cursor: 'pointer'
                    }}>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
